// OPERATOR-CONSOLE: the platform price version's per-plan lines (`plan_prices`): a plan's own
// price for one model (input and output per million tokens), or, for "All models", its own markup
// over our cost. One line per plan and model. Rows have visible Edit and Remove; Add opens a row.
import {
  Button,
  FieldError,
  InfoTip,
  Input,
  NativeSelect,
  NativeSelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { CheckIcon, PencilIcon, PlusIcon, Trash2Icon, XIcon } from "lucide-react";
import { useState } from "react";
import {
  bpText,
  parsePercent,
  parseRate,
  rateText,
  rateToInput,
} from "../../billing/billing-money";
import { DENSE_TABLE, RowAction, RowActions } from "../../settings/row-actions";

export type PlanPrice = {
  plan_key: string;
  model: string | null;
  input_per_m_micros: number | null;
  output_per_m_micros: number | null;
  cache_read_per_m_micros?: number | null | undefined;
  markup_bp: number | null;
};

type Draft = {
  plan: string;
  model: string;
  all: boolean;
  input: string;
  output: string;
  pct: string;
};
const EMPTY: Draft = { plan: "", model: "", all: false, input: "", output: "", pct: "" };

function problemOf(d: Draft, taken: readonly string[]): string | null {
  if (!/^[a-z0-9][a-z0-9_-]{0,63}$/.test(d.plan)) return "Plan: its key (lowercase, e.g. base).";
  const model = d.all ? "" : d.model.trim();
  if (!d.all && !model) return "Model: a model name, or All models.";
  if (taken.includes(`${d.plan}|${model}`)) return "That plan and model are listed already.";
  if (d.all) {
    const bp = parsePercent(d.pct);
    return bp === null || bp > 100_000 ? "Markup: a percent from 0 to 1000." : null;
  }
  if (parseRate(d.input) === null || parseRate(d.output) === null)
    return "Prices: amounts like 0.50 per million tokens.";
  return null;
}

export function PlanPricesEditor({
  value,
  onChange,
  onEditing,
}: {
  value: readonly PlanPrice[];
  onChange: (next: PlanPrice[]) => void;
  onEditing: (editing: boolean) => void;
}) {
  const [editing, setEditing] = useState<number | "new" | null>(null);
  const [draft, setDraft] = useState<Draft>(EMPTY);
  const open = (at: number | "new") => {
    const p = at === "new" ? null : value[at];
    setDraft(
      p
        ? {
            plan: p.plan_key,
            model: p.model ?? "",
            all: p.model === null,
            input: p.input_per_m_micros === null ? "" : rateToInput(p.input_per_m_micros),
            output: p.output_per_m_micros === null ? "" : rateToInput(p.output_per_m_micros),
            pct: p.markup_bp === null ? "" : bpText(p.markup_bp).replace("%", ""),
          }
        : EMPTY,
    );
    setEditing(at);
    onEditing(true);
  };
  const close = () => {
    setEditing(null);
    onEditing(false);
  };
  const taken = value.filter((_, i) => i !== editing).map((p) => `${p.plan_key}|${p.model ?? ""}`);
  const problem = editing === null ? null : problemOf(draft, taken);
  const save = () => {
    if (problem || editing === null) return;
    const prior = editing === "new" ? undefined : value[editing];
    const cacheRead = prior?.cache_read_per_m_micros;
    const line: PlanPrice = draft.all
      ? {
          plan_key: draft.plan,
          model: null,
          input_per_m_micros: null,
          output_per_m_micros: null,
          markup_bp: parsePercent(draft.pct) ?? 0,
        }
      : {
          plan_key: draft.plan,
          model: draft.model.trim(),
          input_per_m_micros: parseRate(draft.input) ?? 0,
          output_per_m_micros: parseRate(draft.output) ?? 0,
          ...(cacheRead != null ? { cache_read_per_m_micros: cacheRead } : {}),
          markup_bp: null,
        };
    onChange(
      editing === "new" ? [...value, line] : value.map((p, i) => (i === editing ? line : p)),
    );
    close();
  };
  const editRow = (key: string) => (
    <TableRow key={key} data-plan-price-edit className="align-top">
      <TableCell>
        <Input
          aria-label="Plan key"
          value={draft.plan}
          placeholder="base"
          onChange={(e) => setDraft({ ...draft, plan: e.target.value.trim().toLowerCase() })}
        />
      </TableCell>
      <TableCell>
        <div className="grid gap-1">
          <NativeSelect
            aria-label="Applies to"
            size="sm"
            value={draft.all ? "all" : "model"}
            onChange={(e) => setDraft({ ...draft, all: e.target.value === "all" })}
          >
            <NativeSelectOption value="model">One model</NativeSelectOption>
            <NativeSelectOption value="all">All models</NativeSelectOption>
          </NativeSelect>
          {draft.all ? null : (
            <Input
              aria-label="Model"
              value={draft.model}
              placeholder="deepseek-flash"
              onChange={(e) => setDraft({ ...draft, model: e.target.value })}
            />
          )}
        </div>
      </TableCell>
      {draft.all ? (
        <TableCell colSpan={2}>
          <Input
            aria-label="Plan markup %"
            inputMode="decimal"
            value={draft.pct}
            placeholder="40"
            onChange={(e) => setDraft({ ...draft, pct: e.target.value })}
          />
        </TableCell>
      ) : (
        <>
          <TableCell>
            <Input
              aria-label="Plan input $ / 1M"
              inputMode="decimal"
              value={draft.input}
              placeholder="0.50"
              onChange={(e) => setDraft({ ...draft, input: e.target.value })}
            />
          </TableCell>
          <TableCell>
            <Input
              aria-label="Plan output $ / 1M"
              inputMode="decimal"
              value={draft.output}
              placeholder="2.00"
              onChange={(e) => setDraft({ ...draft, output: e.target.value })}
            />
          </TableCell>
        </>
      )}
      <TableCell className="w-px">
        <span className="flex items-center gap-0.5">
          <RowAction
            icon={CheckIcon}
            label="Save plan price"
            tip="Save"
            disabled={problem !== null}
            onClick={save}
          />
          <RowAction icon={XIcon} label="Cancel plan price" tip="Cancel" onClick={close} />
        </span>
      </TableCell>
    </TableRow>
  );
  return (
    <div className="flex flex-col gap-2" data-slot="plan-prices">
      <div className="flex items-center justify-between gap-2">
        <span className="inline-flex items-center gap-1 text-sm font-medium">
          Per-plan prices
          <InfoTip label="About per-plan prices">
            A plan's own price for a model, or its own markup over our cost for all models. These
            come before the model prices and markups of this version; a company's own prices still
            win.
          </InfoTip>
        </span>
        <Button
          type="button"
          size="xs"
          variant="secondary"
          disabled={editing !== null}
          onClick={() => open("new")}
        >
          <PlusIcon data-icon="inline-start" aria-hidden="true" />
          Add plan price
        </Button>
      </div>
      <Table className={DENSE_TABLE}>
        <TableHeader>
          <TableRow>
            <TableHead scope="col">Plan</TableHead>
            <TableHead scope="col">Model</TableHead>
            <TableHead scope="col" className="text-right">
              Input / 1M
            </TableHead>
            <TableHead scope="col" className="text-right">
              Output / 1M
            </TableHead>
            <TableHead scope="col">
              <span className="sr-only">Actions</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {value.map((p, i) => {
            const name = `${p.plan_key} ${p.model ?? "all models"}`;
            return editing === i ? (
              editRow(name)
            ) : (
              <TableRow key={name} data-plan-price={name} className="group/row">
                <TableHead scope="row" className="font-mono text-xs font-normal">
                  {p.plan_key}
                </TableHead>
                <TableCell>{p.model ?? "All models"}</TableCell>
                {p.model === null ? (
                  <TableCell colSpan={2} className="text-right tabular-nums">
                    Markup {bpText(p.markup_bp ?? 0)}
                  </TableCell>
                ) : (
                  <>
                    <TableCell className="text-right tabular-nums">
                      {rateText(p.input_per_m_micros ?? 0)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {rateText(p.output_per_m_micros ?? 0)}
                    </TableCell>
                  </>
                )}
                <TableCell className="w-px text-right">
                  <RowActions>
                    <RowAction
                      icon={PencilIcon}
                      label={`Edit ${name}`}
                      tip="Edit"
                      disabled={editing !== null}
                      onClick={() => open(i)}
                    />
                    <RowAction
                      icon={Trash2Icon}
                      label={`Remove ${name}`}
                      tip="Remove"
                      disabled={editing !== null}
                      onClick={() => onChange(value.filter((_, j) => j !== i))}
                    />
                  </RowActions>
                </TableCell>
              </TableRow>
            );
          })}
          {editing === "new" ? editRow("__new") : null}
          {value.length === 0 && editing === null ? (
            <TableRow>
              <TableCell colSpan={5} className="text-muted-foreground">
                None: every plan pays the list prices.
              </TableCell>
            </TableRow>
          ) : null}
        </TableBody>
      </Table>
      {problem && editing !== null ? <FieldError>{problem}</FieldError> : null}
    </div>
  );
}
